/* eslint-disable react/jsx-key */
import React, { useCallback, useMemo, useState } from 'react';
import { Redirect, useParams, useHistory } from 'react-router';
import { Button, Col, Form, InputNumber, Row, Select, Switch, Typography, message as messageAnt } from 'antd-v5';
import { connect } from 'react-redux';
import { useQuery, useQueryClient } from 'react-query';
import StandardLayoutV2 from '../../../../../Layout/StandardLayoutV2';
import {
  INQUIRY_MESSAGES,
  getInquiryAutoMessages,
  updateInquiryAutoMessage,
} from '../../../../../../api/rolexAutoMessages';
import TextAreaWithActions from '../../../../../../components/Layout/TextAreaWithActions';
import '../../../../../../components/Layout/chat/chat.scss';
import { FakeChatBalloon } from '../../../../../Layout/FakeChatBalloon';

const TIMING_OPTIONS = [
  { value: 0, label: 'Immediately' },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1} hour${i + 1 > 1 ? 's' : ''}`,
  })),
];

const RolexSettingsMessage = ({ storeName, merchantName, associate }) => {
  const params = useParams();
  const { push } = useHistory();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const message = Form.useWatch('messageTemplate', form);

  const { data: config, isFetching } = useQuery(
    ['inquiryAutoMessage', params?.msg_type],
    async () => {
      try {
        const response = await getInquiryAutoMessages();
        const msgDef = INQUIRY_MESSAGES.find(item => item.id === params?.msg_type);
        if (!msgDef) return null;

        const existing = response[msgDef.id] || {};
        return {
          ...msgDef,
          dbId: existing.id,
          message: existing.msg || msgDef.defaultMessage || '',
          enabled: existing.active === 1,
          actionScale: existing.action_scale ?? (params?.msg_type === 'new_inquiry' ? 0 : 1),
        };
      } catch (e) {
        console.error('Error fetching inquiry auto message:', e);
        return null;
      }
    },
    {
      refetchOnWindowFocus: false,
      initialData: null,
    },
  );

  const description = useMemo(() => {
    switch (params?.msg_type) {
      case 'new_inquiry':
        return 'This message is sent to clients after a new inquiry with WAITING status is created, based on the configured delay.';
      case 'inquiry_followup':
        return 'This follow-up message is sent to clients with a WAITING inquiry after the configured delay.';
      default:
        return '';
    }
  }, [params?.msg_type]);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      const values = form.getFieldsValue();
      const isNewInquiry = params?.msg_type === 'new_inquiry';
      await updateInquiryAutoMessage(
        config?.dbId,
        {
          msg: values.messageTemplate,
          active: values.enabled ? 1 : 0,
          action_scale: values.actionScale,
          action_interval: isNewInquiry ? 'HOUR' : 'MONTH',
        },
        params?.msg_type,
      );
      queryClient.invalidateQueries('inquiryAutoMessages');
      queryClient.invalidateQueries(['inquiryAutoMessage', params?.msg_type]);
      messageAnt.success('Inquiry message saved successfully.');
    } catch (e) {
      console.error('Error saving inquiry auto message:', e);
      messageAnt.error('Failed to save inquiry message. Please try again.');
    } finally {
      setSaving(false);
      push('/Configuration/rolex');
    }
  }, [form, config?.dbId, params?.msg_type, push, queryClient]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    push('/Configuration/rolex');
  }, [form, push]);

  if (INQUIRY_MESSAGES.findIndex(item => item.id === params?.msg_type) === -1) {
    return <Redirect to="/Configuration/rolex" />;
  }

  return (
    <StandardLayoutV2
      title={config ? `${config?.title}` : 'Inquiry Message'}
      classProps="flex-col-left"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent' }}
      loading={isFetching}
      breadcrumb={[
        { title: 'Setup' },
        { title: 'Rolex Inquiry', path: '/Configuration/rolex' },
        { title: 'Edit Message' },
      ]}
      actions={
        <div className="flex-row items-center justify-end gap-8">
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={isFetching}
            data-testid="inquiries__config-message--save-button"
          >
            Save
          </Button>
          <Button
            onClick={handleCancel}
            disabled={isFetching || saving}
            data-testid="inquiries__config-message--cancel-button"
          >
            Cancel
          </Button>
        </div>
      }
    >
      <Row gutter={32} className="w-100-P">
        <Col span={12}>
          <Form
            form={form}
            layout="vertical"
            className="flex-col-left w-100-P gap-8"
            initialValues={{
              enabled: config?.enabled,
              messageTemplate: config?.message,
              actionScale: config?.actionScale,
            }}
          >
            <div className="flex-row items-center gap-8">
              <Form.Item name="enabled" valuePropName="checked" noStyle>
                <Switch defaultChecked={config?.enabled} data-testid="inquiries__config-message--active-switch" />
              </Form.Item>
              <span>Active</span>
            </div>
            <Typography.Text type="secondary" style={{ textAlign: 'left' }}>
              {description}
            </Typography.Text>
            {params?.msg_type === 'new_inquiry' ? (
              <>
                <div className="flex-row items-center gap-8">
                  <Typography.Text>Send After</Typography.Text>
                  <Form.Item name="actionScale" noStyle>
                    <Select
                      options={TIMING_OPTIONS}
                      style={{ width: 150 }}
                      data-testid="inquiries__config-message--timing-select"
                    />
                  </Form.Item>
                </div>
                <Typography.Text type="secondary" style={{ textAlign: 'left', fontSize: '12px' }}>
                  Messages are sent no earlier than the selected time but may be delayed due to legal sending
                  restrictions. All messages will be delivered within 24 hours to meet Rolex compliance mandates.
                </Typography.Text>
              </>
            ) : (
              <>
                <div className="flex-row items-center gap-8">
                  <Typography.Text>Send Every</Typography.Text>
                  <Form.Item name="actionScale" noStyle>
                    <InputNumber
                      min={1}
                      style={{ width: 70 }}
                      data-testid="inquiries__config-message--interval-input"
                    />
                  </Form.Item>
                  <Typography.Text>month(s)</Typography.Text>
                </div>
                <Typography.Text type="secondary" style={{ textAlign: 'left', fontSize: '12px' }}>
                  This message repeats at the same interval for as long as the inquiry remains in WAITING status.
                </Typography.Text>
              </>
            )}
            <TextAreaWithActions
              rolexInquiry
              hideImages
              hideReviewLink
              containerStyleProps={{ border: '1px solid #d9d9d9', borderRadius: '6px', width: '100%' }}
              val={form.getFieldValue('messageTemplate')}
              changeHandler={value => form.setFieldValue('messageTemplate', value)}
              template
              associate=":associate:"
              hasResetTemplate
              resetTemplateMessage={config?.defaultMessage || ''}
            />
            <Form.Item name="messageTemplate" noStyle />
          </Form>
        </Col>
        <Col span={12}>
          <FakeChatBalloon
            message={message}
            associate={associate}
            storeName={storeName}
            merchantName={merchantName}
            placeholderReplacements={{
              ':model_name:': 'Sky-Dweller',
              ':model_link:': 'https://www.rolex.com/en-us/watches/sky-dweller/m336935-0008',
            }}
          />
        </Col>
      </Row>
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => {
  const storeName = state?.auth?.stores?.find(store => store.id === state.auth.selectedStore)?.name || '';

  return {
    storeName,
    merchantName: state?.auth?.merchantName || '',
    associate: state?.auth?.firstName || '',
  };
};
export default connect(mapStateToProps, {})(RolexSettingsMessage);
